import { useEffect, useRef } from 'react';

import useForage from 'contexts/ForageContext/useForage';
import { LoadingIcon } from 'honeydew/Loader';
import profileStyles from 'pages/account/Profile/styles';
import EBTCardForm from 'shared/ebt/EBTCardForm';
import { trackEvent, updateProfile } from 'utils/analyticsUtils';
import logError from 'utils/errorUtils';

/**
 * Form component for editing EBT card information using Forage payment processing.
 * Handles card tokenization, validation, and error states.
 *
 * @param {Object} props - Component props
 * @param {string} props.customerID - Customer identifier for Forage initialization
 * @param {boolean} props.isSaving - Whether the save operation is in progress
 * @param {Function} props.onSuccess - Callback executed after successful card save
 * @param {Function} props.saveCardWithTokenization - Function to save card with token
 * @param {Object} props.saveError - Error object from save operation
 * @param {Function} props.setIsEditing - Function to exit edit mode
 * @param {boolean} props.isEdit - Whether this is editing an existing card (vs adding new)
 */
export default function EBTCardEdit({
    clearErrors,
    customerID,
    isEdit = false,
    isSaving,
    onSuccess,
    saveCardWithTokenization,
    saveError,
    setIsEditing,
}) {
    const { clearForage, forage, initializeForage, isLoading } = useForage();

    const initAttempted = useRef(false);

    useEffect(() => {
        if (!initAttempted.current && customerID && !forage) {
            initAttempted.current = true;

            const init = async () => {
                try {
                    await initializeForage(customerID);
                } catch (err) {
                    initAttempted.current = false; // Allow retry
                }
            };

            init();
        }
    }, [customerID, initializeForage, forage]);

    const handleSaveCard = async (cardElement) => {
        try {
            await saveCardWithTokenization(forage, cardElement);

            trackEvent(isEdit ? 'SNAP card update' : 'SNAP card add', {
                location: 'account',
            });
            updateProfile({ SNAP_enabled: true });

            clearForage();
            onSuccess?.();
        } catch (err) {
            logError(`Error saving EBT card: ${err?.message || 'Unknown error'} `, {
                elementID: 'account-ebt-card-element-container',
            });
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        clearForage();
    };

    if (isLoading) {
        return (
            <div className={profileStyles.editContainer}>
                <p className="mt-20 flex items-center gap-10 lg:mt-0">
                    <LoadingIcon /> Loading...
                </p>
            </div>
        );
    }

    return (
        <div className={`${profileStyles.editContainer} mt-20 lg:mt-0`}>
            <div>
                <EBTCardForm
                    elementID="account-ebt-card-element-container"
                    isSaving={isSaving}
                    onSave={handleSaveCard}
                    saveError={saveError}
                    clearErrors={clearErrors}
                    submitButtonText="Save"
                />
                <button type="button" className="link text-body-sm mx-auto mt-15 block" onClick={handleCancel}>
                    Cancel
                </button>
            </div>
        </div>
    );
}
